import React from 'react';
import { useGetRetentionOverviewQuery } from '../../store/api/localApi';
import { RETENTION_RECORD_TYPE_LABELS } from '../../types/retention';

/**
 * Retention overview (Task 21) — INFORMATIONAL ONLY. Per record type, how many
 * records are past their computed expiry today. NOTHING IS DELETED by this view;
 * the "nothing is deleted" note is shown prominently. Children under a legal
 * hold are excluded from the counts (their clocks are frozen), and every
 * provenance string is rendered.
 */
const RetentionOverview: React.FC<{ locationId: string }> = ({ locationId }) => {
  const { data, isLoading, isError } = useGetRetentionOverviewQuery({ locationId });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading retention overview…</p>;
  }
  if (isError || !data) {
    return (
      <p data-testid="retention-overview-error" className="text-sm text-red-600">
        Could not load the retention overview.
      </p>
    );
  }

  return (
    <section data-testid="retention-overview" className="mt-10">
      <h3 className="text-lg font-semibold text-gray-900">Retention overview</h3>
      <p
        data-testid="retention-overview-note"
        className="mt-1 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800"
      >
        {data.note} Counts are as of {data.today}.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-4 font-medium">Record type</th>
              <th className="py-2 pr-4 font-medium">Past expiry</th>
              <th className="py-2 pr-4 font-medium">On legal hold</th>
              <th className="py-2 font-medium">Provenance</th>
            </tr>
          </thead>
          <tbody>
            {data.overview.map((row) => (
              <tr
                key={row.recordType}
                data-testid={`retention-overview-row-${row.recordType}`}
                className="border-t border-gray-200"
              >
                <td className="py-3 pr-4 align-top font-medium text-gray-900">
                  {RETENTION_RECORD_TYPE_LABELS[row.recordType]}
                </td>
                <td className="py-3 pr-4 align-top">
                  <span
                    data-testid={`retention-overview-${row.recordType}`}
                    className="font-semibold text-gray-900"
                  >
                    {row.enumerated ? row.pastExpiryCount : '—'}
                  </span>
                  {!row.enumerated && <div className="text-xs text-gray-400">not yet counted</div>}
                  {row.periodMonths === null && (
                    <div className="text-xs text-gray-400">held (no expiry)</div>
                  )}
                </td>
                <td className="py-3 pr-4 align-top text-gray-700">
                  <span data-testid={`retention-overview-held-${row.recordType}`}>
                    {row.heldCount}
                  </span>
                </td>
                <td className="py-3 align-top">
                  <p
                    data-testid={`retention-overview-provenance-${row.recordType}`}
                    className="max-w-md text-xs text-gray-600"
                  >
                    {row.provenance}
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default RetentionOverview;
